import React from 'react'
import { cn } from '../lib/utils.ts'

interface FunnelStage {
  label: string
  value: number | string
  subtitle?: string
  color: FunnelColor
}

interface PipelineFunnelProps {
  stages: FunnelStage[]
  className?: string
  title?: string
}

const colorMap = {
  blue: 'bg-blue-50 border-blue-200 text-blue-700',
  green: 'bg-green-50 border-green-200 text-green-700',
  amber: 'bg-amber-50 border-amber-200 text-amber-700',
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  purple: 'bg-purple-50 border-purple-200 text-purple-700',
  orange: 'bg-orange-50 border-orange-200 text-orange-700',
  red: 'bg-red-50 border-red-200 text-red-700',
} as const

export type FunnelColor = keyof typeof colorMap

const arrowColorMap: Record<FunnelColor, string> = {
  blue: 'text-blue-300',
  green: 'text-green-300',
  amber: 'text-amber-300',
  emerald: 'text-emerald-300',
  purple: 'text-purple-300',
  orange: 'text-orange-300',
  red: 'text-red-300',
}

export default function PipelineFunnel({
  stages,
  className,
  title = 'Lead → Customer Pipeline',
}: PipelineFunnelProps) {
  return (
    <div className={cn('bg-white border border-gray-200 rounded-xl p-6', className)}>
      <h3 className="text-sm font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="flex items-center justify-between gap-2 overflow-x-auto">
        {stages.map((stage, index) => (
          <React.Fragment key={stage.label}>
            {/* Stage Card */}
            <div
              className={cn(
                'flex-1 min-w-[120px] rounded-lg border p-4 text-center',
                colorMap[stage.color],
              )}
            >
              <p className="text-2xl font-bold">{stage.value}</p>
              <p className="text-xs font-medium mt-1 opacity-80">{stage.label}</p>
              {stage.subtitle && <p className="text-[10px] mt-0.5 opacity-60">{stage.subtitle}</p>}
            </div>
            {/* Arrow between stages */}
            {index < stages.length - 1 && (
              <div className={cn('flex-shrink-0', arrowColorMap[stage.color])}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}
